import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import NewInstructionModal from "../NewInstructionModal";
import SavedInstructionItem from "../SavedInstructionItem";
import { PlusIcon } from "../../../../icons";
import { backendSettingsService, DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS, } from "../../../../services/backendSettingsService";
function toUiInstruction(item) {
    return {
        id: item.id,
        title: item.title,
        content: item.instruction,
        enabled: item.on,
    };
}
function toBackendInstruction(item) {
    const id = (item.id || "").trim();
    const content = (item.content || "").trim();
    const title = (item.title || "").trim();
    if (!id || !content) {
        return null;
    }
    return {
        id,
        title: title || "Instruction",
        instruction: content,
        on: Boolean(item.enabled),
    };
}
const CarouselChatTab = ({ mode }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingInstruction, setEditingInstruction] = useState(null);
    const [instructions, setInstructions] = useState([]);
    const [isHydrated, setIsHydrated] = useState(false);
    const [lastPersistedJson, setLastPersistedJson] = useState("");
    // Hydrate mode-specific instructions whenever tab mode changes.
    useEffect(() => {
        let mounted = true;
        const hydrate = async () => {
            setIsHydrated(false);
            try {
                await backendSettingsService.flushPendingWrites();
                const loaded = await backendSettingsService.getCustomInstructions(mode);
                if (!mounted) {
                    return;
                }
                const nextInstructions = (loaded.carouselChat || []).map(toUiInstruction);
                setInstructions(nextInstructions);
                setLastPersistedJson(JSON.stringify(nextInstructions));
            }
            catch (error) {
                if (!mounted) {
                    return;
                }
                const cached = backendSettingsService.getCachedCustomInstructions(mode);
                const nextInstructions = (cached?.carouselChat || []).map(toUiInstruction);
                setInstructions(nextInstructions);
                setLastPersistedJson(JSON.stringify(nextInstructions));
                console.warn(`[CarouselChatTab] Falling back to cached instructions for ${mode}:`, error);
            }
            setIsHydrated(true);
        };
        void hydrate();
        return () => {
            mounted = false;
        };
    }, [mode]);
    // Write-through updates after initial hydration.
    useEffect(() => {
        if (!isHydrated) {
            return;
        }
        const nextJson = JSON.stringify(instructions);
        if (nextJson === lastPersistedJson) {
            return;
        }
        const persist = async () => {
            const cached = backendSettingsService.getCachedCustomInstructions(mode) || {
                ...DEFAULT_BACKEND_CUSTOM_INSTRUCTIONS,
                carouselChat: [],
                search: [],
                research: [],
                execution: [],
            };
            const nextCarouselChat = instructions
                .map(toBackendInstruction)
                .filter((item) => Boolean(item));
            try {
                await backendSettingsService.putCustomInstructions(mode, {
                    ...cached,
                    carouselChat: nextCarouselChat,
                });
                setLastPersistedJson(nextJson);
            }
            catch (error) {
                // Fail-open: local UI state remains usable, backend will retry later.
                console.warn(`[CarouselChatTab] Deferred backend sync for ${mode}:`, error);
            }
        };
        void persist();
    }, [instructions, isHydrated, lastPersistedJson, mode]);
    const handleCreateNew = () => {
        setEditingInstruction(null);
        setIsModalOpen(true);
    };
    const handleSaveInstruction = (title, content) => {
        if (editingInstruction) {
            // Update existing instruction
            setInstructions(instructions.map(inst => inst.id === editingInstruction.id
                ? { ...inst, title, content }
                : inst));
        }
        else {
            // Create new instruction
            const newInstruction = {
                id: Date.now().toString(),
                title,
                content,
                enabled: true,
            };
            setInstructions([...instructions, newInstruction]);
        }
        setEditingInstruction(null);
        setIsModalOpen(false);
    };
    const handleToggle = (id, enabled) => {
        setInstructions(instructions.map(inst => inst.id === id ? { ...inst, enabled } : inst));
    };
    const handleEdit = (id) => {
        const instruction = instructions.find(inst => inst.id === id);
        if (instruction) {
            setEditingInstruction(instruction);
            setIsModalOpen(true);
        }
    };
    const handleDelete = (id) => {
        setInstructions(instructions.filter(inst => inst.id !== id));
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingInstruction(null);
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex flex-col gap-10 w-full", children: _jsxs("div", { className: "flex flex-col gap-3 items-start w-full", children: [_jsxs("div", { className: "flex gap-3 items-center w-full", children: [_jsx("h2", { className: "flex-1 font-sans font-bold leading-normal text-[15px] text-gray-990 m-0", children: "Saved Instructions" }), _jsxs("button", { onClick: handleCreateNew, className: "bg-transparent border-none flex gap-1 items-center justify-center cursor-pointer hover:opacity-80 transition-opacity", type: "button", children: [_jsx("span", { className: "font-sans font-bold leading-normal text-[13px] text-primary-purple-70", children: "Create New Instruction" }), _jsx(PlusIcon, { size: 16, color: "#6050D7" })] })] }), _jsx("p", { className: "font-sans font-normal leading-normal text-[13px] text-gray-600 m-0 w-full", children: instructions.length > 0
                                ? `${instructions.length} saved instruction${instructions.length !== 1 ? 's' : ''}`
                                : 'No saved instructions' }), instructions.length > 0 && (_jsx("div", { className: "flex flex-col gap-6 w-full", children: instructions.map((instruction) => (_jsx(SavedInstructionItem, { instruction: instruction, onToggle: handleToggle, onEdit: handleEdit, onDelete: handleDelete }, instruction.id))) }))] }) }), _jsx(NewInstructionModal, { isOpen: isModalOpen, onClose: handleCloseModal, onSave: handleSaveInstruction, editingInstruction: editingInstruction })] }));
};
export default CarouselChatTab;
